import { useCallback, useEffect, useState } from 'react'
import { TemplateInputParameters } from '../../../../../../models/AiFeature'

const IGNORED_LEXICAL_REGEX = /\s*dir="ltr"\s*|\n/gm

export const useUserTemplateConfigForm = (
  inputParameters: TemplateInputParameters,
  parentInputParameters: TemplateInputParameters,
  isCustomized: boolean,
  onSave: (inputParameters: TemplateInputParameters, isCustomized: boolean) => Promise<void>,
  onHide: () => void,
  disabled: boolean,
) => {
  const [editedInputParameters, setEditedInputParameters] = useState<TemplateInputParameters>(inputParameters)
  const [customized, setCustomized] = useState<boolean>(isCustomized)
  const [isChanged, setIsChanged] = useState<boolean>(false)
  const [showConfirmation, setShowConfirmation] = useState(false)
  const [shouldReset, setShouldReset] = useState<boolean>(false)
  const [showGenerate, setShowGenerate] = useState<boolean>(false)
  const [isGenerating, setGenerating] = useState<boolean>(false)

  const templateHasValue = !!editedInputParameters.template

  useEffect(() => {
    setEditedInputParameters(inputParameters)
  }, [inputParameters])

  useEffect(() => {
    setCustomized(isCustomized)
  }, [isCustomized])

  const handleChangeCustomized = () => {
    setCustomized(!customized)
    if (customized) {
      setEditedInputParameters(parentInputParameters)
    } else {
      setEditedInputParameters(inputParameters)
    }
  }

  const handleClickBack = () => {
    if (showGenerate) {
      setShowGenerate(false)
    } else if (isChanged && !disabled && templateHasValue) {
      setShowConfirmation(true)
    } else {
      onHide()
    }
  }

  const handleClickCancel = () => {
    setShouldReset(true)
    setCustomized(isCustomized)
    setEditedInputParameters(inputParameters)
    setIsChanged(false)
  }

  const handleClickSave = async () => {
    setIsChanged(false)
    await onSave(editedInputParameters, customized)
  }

  const handleChangeTemplate = useCallback(
    (newTemplate: string) => {
      setEditedInputParameters((inputParameters) => ({ ...inputParameters, template: newTemplate.trim() }))
    },
    [setEditedInputParameters],
  )

  const handleChangeExample = useCallback(
    (field: string, newExample: string) => {
      setEditedInputParameters((inputParameters) => ({ ...inputParameters, [field]: newExample.trim() }))
    },
    [setEditedInputParameters],
  )

  const checkForChange = useCallback(() => {
    const { example_1 = '', example_2 = '', template = '' } = inputParameters
    const { example_1: editedExample_1 = '', example_2: editedExample_2 = '', template: editedTemplate = '' } = editedInputParameters
    setIsChanged(
      editedTemplate.trim().replace(IGNORED_LEXICAL_REGEX, '') !== template.trim().replace(IGNORED_LEXICAL_REGEX, '') ||
        editedExample_1.trim().replace(IGNORED_LEXICAL_REGEX, '') !== example_1.trim().replace(IGNORED_LEXICAL_REGEX, '') ||
        editedExample_2.trim().replace(IGNORED_LEXICAL_REGEX, '') !== example_2.trim().replace(IGNORED_LEXICAL_REGEX, '') ||
        customized !== isCustomized,
    )
  }, [editedInputParameters, customized, inputParameters, isCustomized])

  return {
    editedInputParameters,
    templateHasValue,
    isChanged,
    customized,
    showConfirmation,
    shouldReset,
    isGenerating,
    showGenerate,
    setEditedInputParameters,
    setShouldReset,
    setShowConfirmation,
    setShowGenerate,
    setGenerating,
    handleChangeCustomized,
    handleChangeExample,
    handleChangeTemplate,
    handleClickBack,
    handleClickCancel,
    handleClickSave,
    checkForChange,
  }
}
